/**
 * Утилиты для работы с датами и временем
 * 
 * ВРЕМЕНА ОТОБРАЖАЮТСЯ В ЛОКАЛЬНОМ ЧАСОВОМ ПОЯСЕ ПОЛЬЗОВАТЕЛЯ
 * Это обеспечивает корректное отображение времени в соответствии
 * с часовым поясом пользователя
 */

/**
 * Форматирует дату в локальный формат
 * @param {string|Date} date - Дата для форматирования
 * @param {string} locale - Локаль (по умолчанию 'ru-RU')
 * @returns {string} Отформатированная дата
 */
export const formatDate = (date, locale = 'ru-RU') => {
  if (!date) return '';
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString(locale);
};

/**
 * Форматирует дату и время в локальном часовом поясе пользователя
 * @param {string|Date} date - Дата для форматирования
 * @param {string} locale - Локаль (по умолчанию 'ru-RU')
 * @returns {string} Отформатированная дата и время в локальном часовом поясе
 */
export const formatDateTimeLocal = (date, locale = 'ru-RU') => {
  if (!date) return '';
  const dateObj = new Date(date);
  
  return dateObj.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Форматирует дату и время в московском часовом поясе (для обратной совместимости)
 * @param {string|Date} date - Дата для форматирования
 * @param {string} locale - Локаль (по умолчанию 'ru-RU')
 * @returns {string} Отформатированная дата и время в московском часовом поясе
 */
export const formatDateTimeMoscow = (date, locale = 'ru-RU') => {
  if (!date) return '';
  const dateObj = new Date(date);
  
  // Конвертируем в московское время (UTC+3)
  const moscowTime = new Date(dateObj.getTime() + (3 * 60 * 60 * 1000));
  
  return moscowTime.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Moscow'
  });
};

/**
 * Форматирует дату и время
 * @param {string|Date} date - Дата для форматирования
 * @param {string} locale - Локаль (по умолчанию 'ru-RU')
 * @returns {string} Отформатированная дата и время
 */
export const formatDateTime = (date, locale = 'ru-RU') => {
  if (!date) return '';
  const dateObj = new Date(date);
  return dateObj.toLocaleString(locale);
};

/**
 * Форматирует дату в формат месяц-год
 * @param {string|Date} date - Дата для форматирования
 * @param {string} locale - Локаль (по умолчанию 'ru-RU')
 * @returns {string} Отформатированная дата в формате месяц-год
 */
export const formatDateToMonthYear = (date, locale = 'ru-RU') => {
  if (!date) return '';
  const dateObj = new Date(date);
  const options = { year: 'numeric', month: 'long' };
  return dateObj.toLocaleDateString(locale, options);
};

/**
 * Проверяет, является ли дата сегодняшней
 * @param {string|Date} date - Дата для проверки
 * @returns {boolean} true, если дата сегодняшняя
 */
export const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  const dateObj = new Date(date);
  return today.toDateString() === dateObj.toDateString();
};

/**
 * Получает относительное время (например, "2 часа назад")
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} Относительное время
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const dateObj = new Date(date);
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  if (diffInSeconds < 60) return 'только что';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} мин. назад`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ч. назад`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} дн. назад`;
  
  return formatDate(date);
}; 